import React, { useState } from "react";
import Api from "../../../../core/data/redux/utils/apiCall";
import { BASE_URLS } from "../../../../core/data/redux/apis/urls";

interface VpfApprovalProps {
  id: string;
  nocData: any;
  vpfItem: any;
  onRefresh: () => void;
  onBack: () => void;
  onShowToast: (title: string, message: string, type: "success" | "danger" | "warning" | "primary") => void;
}

export const VpfApproval: React.FC<VpfApprovalProps> = ({
  id,
  nocData,
  vpfItem,
  onRefresh,
  onBack,
  onShowToast,
}) => {
  const [isProcessing, setIsProcessing] = useState(false);
  const [processingAction, setProcessingAction] = useState<"A" | "R" | null>(null);

  if (!vpfItem) {
    return (
      <div className="page-wrapper">
        <div className="content">
          <div className="row justify-content-center">
            <div className="col-md-8">
              <div className="card">
                <div className="card-body text-center py-5">
                  <i className="ti ti-file-x display-4 text-muted"></i>
                  <h5 className="mt-3 text-muted">No VPF Data Available</h5>
                  <p className="text-muted">No VPF data found for the requested ID.</p>
                  <button onClick={onBack} className="btn btn-primary">
                    <i className="ti ti-arrow-left me-2"></i>Go Back
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const handleVpfApproveReject = async (action: "A" | "R") => {
    if (!vpfItem?.requestID) {
      onShowToast("Error", "VPF Request ID not found", "danger");
      return;
    }

    setIsProcessing(true);
    setProcessingAction(action);

    try {
      const payload = {
        Action: action,
        ReferenceNumber: vpfItem.requestID,
      };

      const res: any = await Api.post(`${BASE_URLS.BASE_URL}vpf-request/approve_reject_action`, payload);
      if (res?.data) {
        const actionText = action === "A" ? "approved" : "rejected";
        onShowToast("Success", `VPF request ${actionText} successfully`, "success");
        setTimeout(() => {
          onBack();
        }, 1500);
      }
    } catch (err: any) {
      onShowToast("Error", err?.message || "Failed to process VPF request", "danger");
    } finally {
      setIsProcessing(false);
      setProcessingAction(null);
    }
  };

  return (
    <div className="page-wrapper">
      <div className="content">
        <div className="d-md-flex d-block align-items-center justify-content-between mb-3">
          <div className="my-auto mb-2">
            <div className="d-flex align-items-center gap-2">
              <i
                className="ti ti-arrow-left"
                onClick={onBack}
                style={{ cursor: "pointer", fontSize: "1.5rem" }}
              ></i>
              <h3 className="page-title mb-0">VPF Request Approval</h3>
            </div>
            <nav className="mt-2">
              <ol className="breadcrumb mb-0">
                <li className="breadcrumb-item">
                  <a href="#" onClick={(e) => { e.preventDefault(); onBack(); }}>Tasks</a>
                </li>
                <li className="breadcrumb-item active" aria-current="page">VPF Details</li>
              </ol>
            </nav>
          </div>
          <div className="d-flex gap-2 align-items-center">
            {nocData?.RequestStatus === "T" && (
              <>
                <button
                  className="btn btn-success"
                  onClick={() => handleVpfApproveReject("A")}
                  disabled={isProcessing}
                >
                  {isProcessing && processingAction === "A" ? "Processing..." : "Approve"}
                </button>
                <button
                  className="btn btn-danger"
                  onClick={() => handleVpfApproveReject("R")}
                  disabled={isProcessing}
                >
                  {isProcessing && processingAction === "R" ? "Processing..." : "Reject"}
                </button>
              </>
            )}
            <button onClick={onBack} className="btn btn-outline-secondary">
              <i className="ti ti-arrow-left me-2"></i>Go Back
            </button>
          </div>
        </div>

        {/* Status Banner */}
        <div className="alert alert-warning d-flex align-items-center mb-4">
          <i className="ti ti-clock me-3 text-warning fs-24"></i>
          <div>
            <div className="text-muted small">Request Status</div>
            <div className="fw-bold">{nocData?.RequestStatus === "T" ? "To Be Approved" : nocData?.RequestStatus || vpfItem.status || "Pending"}</div>
          </div>
        </div>

        {/* VPF Details */}
        <div className="card mb-4 border-0 shadow-sm">
          <div className="card-header bg-light">
            <h5 className="card-title mb-0">
              <i className="ti ti-wallet me-2 text-primary"></i>VPF Details
            </h5>
          </div>
          <div className="card-body">
            <div className="row g-3">
              <div className="col-md-6 col-sm-6">
                <label className="text-muted fw-semibold mb-1">Request Creation Date</label>
                <div className="text-dark fw-medium">
                  {nocData?.created_at ? new Date(nocData.created_at).toLocaleDateString("en-GB").replace(/\//g, "-") : vpfItem.creationDate || "-"}
                </div>
              </div>
              <div className="col-md-6 col-sm-6">
                <label className="text-muted fw-semibold mb-1">Amount</label>
                <div className="text-dark fw-bold text-success">
                  ₹{nocData?.VpfAmount ? parseFloat(nocData.VpfAmount).toFixed(3) : "0.000"}
                </div>
              </div>
              <div className="col-md-6 col-sm-6">
                <label className="text-muted fw-semibold mb-1">Month</label>
                <div className="text-dark fw-medium">{nocData?.DeductionFromTheMonth || "-"}</div>
              </div>
              <div className="col-md-6 col-sm-6">
                <label className="text-muted fw-semibold mb-1">Year</label>
                <div className="text-dark fw-medium">{nocData?.DeductionFromTheYear || "-"}</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
